"use client";

import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { FcGoogle } from "react-icons/fc";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAppContext } from "@/contexts/app";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { signIn } from "next-auth/react";
import NextImage from "next/image";
import { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

type SignModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export default function SignModal({ open, onOpenChange }: SignModalProps) {
  const { showSignModal, setShowSignModal } = useAppContext();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolvedOpen = open ?? showSignModal ?? false;
  const resolvedOnOpenChange = onOpenChange ?? setShowSignModal ?? (() => undefined);

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signIn("google", { callbackUrl: window.location.href });
    } catch (err) {
      setError("Sign in failed. Please try again.");
      setIsLoading(false);
    }
  };

  const content = (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-6 py-6">
      <div className="flex items-center justify-center rounded-2xl border border-border/50 bg-background/50 p-4 shadow-sm">
        <div className="relative h-16 w-16">
          <NextImage
            src="/favicon.svg"
            alt="Logo"
            fill
            className="object-contain"
          />
        </div>
      </div>

      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          Welcome to Felixcraft
        </h2>
        <p className="text-sm text-muted-foreground">
          Sign in to browse and manage Felixcraft
        </p>
      </div>

      {error ? (
        <Alert variant="destructive" className="w-full">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="w-full">
        <Button
          className={cn(
            "flex h-12 w-full items-center justify-center gap-3 rounded-full bg-primary text-base text-primary-foreground shadow-lg transition-all duration-300 hover:bg-primary/90"
          )}
          onClick={handleSignIn}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <FcGoogle className="h-6 w-6" />
          )}
          <span className="font-medium">
            {isLoading ? "Signing in..." : "Continue with Google"}
          </span>
        </Button>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={resolvedOpen} onOpenChange={resolvedOnOpenChange}>
        <DialogContent className="sm:max-w-[425px] rounded-3xl border border-border/70 bg-card/95 p-0 shadow-sm">
          <DialogTitle className="sr-only">Sign In</DialogTitle>
          <div className="p-8">{content}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={resolvedOpen} onOpenChange={resolvedOnOpenChange}>
      <DrawerContent className="rounded-t-3xl border-t border-border/70 bg-card/95 shadow-sm">
        <DrawerTitle className="sr-only">Sign In</DrawerTitle>
        <div className="px-6 pb-12 pt-8">
          {content}
          <div className="mt-8">
            <DrawerClose asChild>
              <Button variant="ghost" className="w-full rounded-full">
                Cancel
              </Button>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
